import type { Ctx } from '@milkdown/kit/ctx';
import { $ctx } from '@milkdown/kit/utils';
import { editorViewCtx } from '@milkdown/kit/core';
import { renderToStaticMarkup } from 'react-dom/server';
import { Video } from '@phosphor-icons/react';

import type { MediaUploadHandler } from '../../types';
import { defaultMediaUpload, runEditorMediaUpload } from '../../plugins/runEditorMediaUpload';
import { VIDEO_NODE_NAME } from './video';

/** Type for slash menu item configuration (matches Crepe's SlashMenuItem) */
export interface SlashMenuItemConfig {
  label: string;
  icon: string;
  onRun: (ctx: Ctx) => void;
}

/**
 * Video upload configuration context slice.
 * Store the upload callback here so slash items can access it.
 */
export interface VideoConfig {
  onUploadVideo?: MediaUploadHandler;
}

export const videoConfig = $ctx<VideoConfig, 'videoConfig'>({ onUploadVideo: undefined }, 'videoConfig');

const ACCEPTED_VIDEO_TYPES = 'video/mp4,video/webm,video/ogg,video/quicktime';

function insertVideoFromFilePicker(ctx: Ctx) {
  const view = ctx.get(editorViewCtx);
  const uploadFn = ctx.get(videoConfig.key).onUploadVideo ?? defaultMediaUpload;

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPTED_VIDEO_TYPES;
  input.style.display = 'none';

  const cleanupInput = () => input.remove();
  input.addEventListener('cancel', cleanupInput);

  input.addEventListener('change', () => {
    cleanupInput();
    const file = input.files?.[0];
    if (!file) return;

    const { $from } = view.state.selection;
    const blockStart = $from.before($from.depth);
    const blockEnd = $from.after($from.depth);

    runEditorMediaUpload({
      view,
      file,
      kind: 'video',
      widgetPos: blockStart,
      replaceRange: { from: blockStart, to: blockEnd },
      upload: uploadFn,
      logLabel: '[Video]',
      createNode(src, f) {
        const videoNodeType = view.state.schema.nodes[VIDEO_NODE_NAME];
        if (!videoNodeType) {
          console.error('[Video] video node type not found in schema');

          return null;
        }

        return videoNodeType.create({
          src,
          title: f.name,
          width: 640,
          height: 360,
        });
      },
      afterSuccess: () => view.focus(),
    });
  });

  document.body.appendChild(input);
  input.click();
}

/**
 * Slash menu item for inserting videos.
 * Opens native file picker, uploads video, and inserts node.
 */
export const VideoSlashMenuItems = {
  video: (): SlashMenuItemConfig => ({
    label: 'Video',
    icon: renderToStaticMarkup(<Video size={20} weight="fill" className="text-purple-500" />),
    onRun: (ctx: Ctx) => {
      insertVideoFromFilePicker(ctx);
    },
  }),
};
